import * as Sentry from "@sentry/react";

/**
 * Error messages Sentry drops before sending (substring match).
 * - "IABInnerFrame": thrown by a script the Instagram/Facebook in-app browser
 *   injects into the page (`window._META.IABInnerFrame`), not by our code.
 *   FRANKLYCATERING-F, #1554.
 */
export const SENTRY_IGNORE_ERRORS = ["IABInnerFrame"];

/**
 * Initialize Sentry in production only. No-op in dev/test.
 * Safe to call when VITE_SENTRY_DSN is unset — Sentry will silently disable.
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn || import.meta.env.MODE !== "production") return;

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_RELEASE_VERSION,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    initialScope: { tags: { app: "fcat" } },
    ignoreErrors: SENTRY_IGNORE_ERRORS,
    beforeSend(event) {
      const hdrs = event.request?.headers as Record<string, string> | undefined;
      if (hdrs) {
        for (const k of Object.keys(hdrs)) {
          if (/^(authorization|apikey|x-supabase-api-key|cookie|x-access-token)$/i.test(k)) {
            delete hdrs[k];
          }
        }
      }
      const redactInObj = (obj: unknown): unknown => {
        if (!obj || typeof obj !== "object") return obj;
        if (Array.isArray(obj)) return obj.map(redactInObj);
        const o = obj as Record<string, unknown>;
        for (const k of Object.keys(o)) {
          if (/(access_token|refresh_token|authorization|apikey|password)/i.test(k)) {
            o[k] = "[redacted]";
          } else {
            o[k] = redactInObj(o[k]);
          }
        }
        return o;
      };
      if (event.request?.data) event.request.data = redactInObj(event.request.data) as string;
      if (event.extra) event.extra = redactInObj(event.extra) as Record<string, unknown>;
      return event;
    },
  });
}

export { Sentry };
